// src/components/admin/EventoForm.js
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../../components/ui/Label';
import { DatePicker } from '../ui/DatePicker';
import { toast } from 'react-toastify';
import { createEvento, updateEvento, uploadEventoImage, deleteEventoImage, getFullApiBaseUrl } from '../../services/api';
import { format, parseISO } from 'date-fns';
import RichTextEditor from '../ui/RichTextEditor'; // RE-IMPORTADO
import { Image as ImageIcon, Trash2, Upload, Loader2, Bell } from 'lucide-react';
import Swal from 'sweetalert2';
import ImageCropperModal from './ImageCropperModal';
import DOMPurify from 'dompurify'; // NOVO IMPORT

// Proporção ideal: 3508 x 1665 (aproximadamente 2.1069:1)
const TARGET_ASPECT_RATIO = 3508 / 1665;

function EventoForm({ evento, onSuccess, onCancel }) {
    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        dataInicio: undefined,
        dataFim: undefined,
        cargaHorariaTotal: '',
        local: '',
        ativo: true,
        imagemUrl: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [notificarParticipantes, setNotificarParticipantes] = useState(false);
    const fileInputRef = useRef(null);

    // Estados para o Cropper Modal
    const [showCropperModal, setShowCropperModal] = useState(false);
    const [fileToCrop, setFileToCrop] = useState(null);

    const isNewEvent = !evento?.id;

    useEffect(() => {
        if (evento) {
            // Sanitiza o HTML antes de o passar para o editor
            const safeHtml = evento.descricao ? DOMPurify.sanitize(evento.descricao) : '';

            setFormData({
                nome: evento.nome || '',
                descricao: safeHtml, // Usa o HTML sanitizado
                dataInicio: evento.dataInicio ? parseISO(evento.dataInicio) : undefined,
                dataFim: evento.dataFim ? parseISO(evento.dataFim) : undefined,
                cargaHorariaTotal: evento.cargaHorariaTotal || '',
                local: evento.local || '',
                ativo: evento.ativo !== undefined ? evento.ativo : true,
                imagemUrl: evento.imagemUrl || '',
            });
        } else {
            setFormData({
                nome: '',
                descricao: '',
                dataInicio: undefined,
                dataFim: undefined,
                cargaHorariaTotal: '',
                local: '',
                ativo: true,
                imagemUrl: '',
            });
        }
        setNotificarParticipantes(false);
    }, [evento]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDateChange = (name, date) => {
        setFormData(prev => ({
            ...prev,
            [name]: date
        }));
    };

    const handleDescriptionChange = (value) => {
        setFormData(prev => ({
            ...prev,
            descricao: value
        }));
    };

    const handleFileSelect = (e) => {
        e.preventDefault(); 
        
        if (isNewEvent) {
            toast.error("Por favor, salve o evento primeiro antes de carregar a imagem.");
            e.target.value = null;
            return;
        }

        const file = e.target.files[0];
        if (!file) return;

        // 1. Define o arquivo para o cropper
        setFileToCrop(file);
        // 2. Abre o modal do cropper
        setShowCropperModal(true);
        // Limpa o input de arquivo para permitir o re-upload
        e.target.value = null;
    };

    const handleCropComplete = async (croppedBlob) => {
        // Esta função é chamada pelo modal após o corte
        if (!croppedBlob) return;

        setIsUploading(true);
        try {
            // Cria um objeto File a partir do Blob para enviar ao backend
            const croppedFile = new File([croppedBlob], "cropped_image.jpeg", { type: croppedBlob.type });

            const data = await uploadEventoImage(evento.id, croppedFile);
            
            setFormData(prev => ({
                ...prev,
                imagemUrl: data.imagemUrl
            }));
            toast.success("Imagem cortada e carregada com sucesso! Salve o evento para finalizar.");
        } catch (err) {
            console.error("Erro ao carregar imagem:", err.message, err); 
            toast.error(err.message || 'Erro ao carregar imagem.');
        } finally {
            setIsUploading(false);
            setFileToCrop(null);
        }
    };

    const handleImageDelete = async () => {
        const result = await Swal.fire({
            title: 'Remover Imagem?',
            text: "Você tem certeza que deseja remover a imagem atual do evento?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, remover!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                if (evento?.id) {
                    await deleteEventoImage(evento.id);
                }
                setFormData(prev => ({ ...prev, imagemUrl: '' }));
                toast.success("Imagem removida com sucesso! Salve o evento para finalizar.");
            } catch (err) {
                console.error("Erro ao remover imagem:", err);
                toast.error(err.message || 'Erro ao remover imagem.');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const payload = {
            ...formData,
            dataInicio: formData.dataInicio ? format(formData.dataInicio, 'yyyy-MM-dd') : null,
            dataFim: formData.dataFim ? format(formData.dataFim, 'yyyy-MM-dd') : null,
            cargaHorariaTotal: Number(formData.cargaHorariaTotal),
            // Adiciona o campo de notificação apenas se for uma atualização
            ...(evento && { notificarParticipantes: notificarParticipantes }) 
        };

        try {
            if (evento) {
                await updateEvento(evento.id, payload);
            } else {
                // Se for um novo evento, criamos e o onSuccess recarrega a lista
                await createEvento(payload);
            }
            onSuccess();
        } catch (err) {
            console.error("Erro no formulário do evento:", err);
            toast.error(err.message || 'Ocorreu um erro ao processar sua requisição.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Função auxiliar para construir o URL completo da imagem
    const getFullImageUrl = (url) => {
        if (!url) return null;
        const baseUrl = getFullApiBaseUrl();
        
        // Remove barras iniciais do URL do evento
        let normalizedUrl = url.startsWith('/') ? url.substring(1) : url;
        
        // Adiciona 'files/' se não estiver presente
        if (!normalizedUrl.startsWith('files/')) {
            normalizedUrl = `files/${normalizedUrl}`;
        }
        
        // Concatena: http://localhost:8080/api/ + files/ + nome_do_arquivo
        return `${baseUrl}/${normalizedUrl}`;
    };

    const fullImageUrl = getFullImageUrl(formData.imagemUrl);

    return (
        <>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-2">
                    <Label htmlFor="nome" className="mb-1">Nome:</Label>
                    <Input
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        required
                    />
                </div>
                
                {/* Campo de Imagem */}
                <div className="grid gap-2">
                    <Label className="mb-1">Imagem do Evento (Obrigatório: Proporção 3508:1665)</Label>
                    
                    {/* Input de arquivo oculto - SEMPRE PRESENTE NO DOM */}
                    <Input 
                        id="file-upload" 
                        type="file" 
                        className="hidden" 
                        onChange={handleFileSelect} 
                        ref={fileInputRef}
                        accept="image/png, image/jpeg, image/webp"
                        disabled={isSubmitting || isUploading || isNewEvent}
                    />

                    {isNewEvent && (
                        <div className="p-4 mb-2 border border-primary/50 rounded-md bg-primary/10 text-sm text-primary font-medium">
                            ⚠️ Por favor, salve o evento primeiro antes de carregar a imagem.
                        </div>
                    )}
                    {fullImageUrl ? (
                        <div className="relative border rounded-md p-2 bg-muted/50 w-full aspect-3508/1665"> {/* Usando aspect-3508/1665 */}
                            <img 
                                src={fullImageUrl} 
                                alt="Pré-visualização do Evento" 
                                className="w-full h-full object-cover object-top rounded-md"
                            />
                            <div className="absolute top-4 right-4 flex space-x-2">
                                <Button type="button" variant="destructive" size="icon" onClick={handleImageDelete} disabled={isSubmitting || isUploading}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                                <Button type="button" variant="secondary" size="icon" onClick={() => fileInputRef.current.click()} disabled={isSubmitting || isUploading}>
                                    <Upload className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center w-full">
                            <Label htmlFor="file-upload" className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isNewEvent ? 'bg-gray-200/50 cursor-not-allowed' : 'bg-muted/50 hover:bg-muted'}`}>
                                {isUploading ? (
                                    <div className="flex items-center text-primary">
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                        A carregar...
                                    </div>
                                ) : (
                                    <>
                                        <ImageIcon className="w-8 h-8 text-muted-foreground mb-2" />
                                        <p className="mb-2 text-sm text-muted-foreground"><span className="font-semibold">Clique para carregar</span> ou arraste e solte</p>
                                        <p className="text-xs text-muted-foreground">PNG, JPG, WEBP (Max 5MB). Proporção: 3508:1665.</p>
                                    </>
                                )}
                            </Label>
                        </div>
                    )}
                </div>

                <div className="grid gap-2">
                    <Label htmlFor="descricao" className="mb-1">Descrição:</Label>
                    <RichTextEditor
                        key={evento?.id || 'new'} // Mantendo a key para forçar o reset do componente
                        value={formData.descricao}
                        onChange={handleDescriptionChange}
                    />
                </div>
                <div className="grid gap-2">
                    <Label htmlFor="local" className="mb-1">Local:</Label>
                    <Input
                        id="local"
                        name="local"
                        value={formData.local}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                        <Label htmlFor="dataInicio" className="mb-1">Data de Início:</Label>
                        <DatePicker
                            date={formData.dataInicio}
                            setDate={(date) => handleDateChange('dataInicio', date)}
                            placeholder="Selecione a data de início"
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="dataFim" className="mb-1">Data de Fim:</Label>
                        <DatePicker
                            date={formData.dataFim}
                            setDate={(date) => handleDateChange('dataFim', date)}
                            placeholder="Selecione a data de fim"
                            disabled={isSubmitting}
                        />
                    </div>
                </div>
                <div className="grid gap-2">
                    <Label htmlFor="cargaHorariaTotal" className="mb-1">Carga Horária Total (horas):</Label>
                    <Input
                        type="number"
                        id="cargaHorariaTotal"
                        name="cargaHorariaTotal"
                        value={formData.cargaHorariaTotal}
                        onChange={handleChange}
                        required
                    />
                </div>
                
                {/* Opções de Status e Notificação */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="ativo"
                            name="ativo"
                            checked={formData.ativo}
                            onChange={handleChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <Label htmlFor="ativo" className="font-normal">Evento Ativo?</Label>
                    </div>
                    
                    {/* Checkbox de Notificação (Apenas para edição) */}
                    {!isNewEvent && (
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="notificarParticipantes"
                                name="notificarParticipantes"
                                checked={notificarParticipantes}
                                onChange={(e) => setNotificarParticipantes(e.target.checked)}
                                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                            />
                            <Label htmlFor="notificarParticipantes" className="font-normal flex items-center">
                                <Bell className="h-4 w-4 mr-1 text-orange-500" /> Notificar inscritos sobre esta atualização?
                            </Label>
                        </div>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
                    <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting || isUploading} className="w-full sm:w-auto">
                        Cancelar
                    </Button>
                    <Button type="submit" disabled={isSubmitting || isUploading} className="w-full sm:w-auto">
                        {isSubmitting ? 'A guardar...' : (evento ? 'Atualizar Evento' : 'Criar Evento')}
                    </Button>
                </div>
            </form>

            {/* Modal do Cropper */}
            {fileToCrop && (
                <ImageCropperModal
                    isOpen={showCropperModal}
                    onClose={() => setShowCropperModal(false)}
                    file={fileToCrop}
                    onCropComplete={handleCropComplete}
                />
            )}
        </>
    );
}

export default EventoForm;